/**
 * Kartenkacheln vorhalten (PV-B01 bis PV-B04).
 *
 * Der einzige Teil, der aktiv vorbereitet werden muss. Da die Adresse beim
 * Anlegen des Projekts bekannt ist, lassen sich die Kacheln rund um das Objekt
 * im Voraus laden. Fuer 200 m Umkreis ueber die zum Zeichnen noetigen
 * Zoomstufen sind rund 2 bis 3 MB zu erwarten — ein Tagespensum an Terminen
 * passt problemlos auf das Geraet.
 *
 * Abgelegt wird in der Cache Storage unter demselben Namen, den der Service
 * Worker fuer Kartenkacheln benutzt. Damit bedient er sie spaeter von dort,
 * ohne dass hier noch etwas passieren muss.
 */

const KACHELSPEICHER = 'kartenkacheln';

export interface Kachel {
  z: number;
  x: number;
  y: number;
}

export function kachelFuer(lat: number, lng: number, z: number): Kachel {
  const n = 2 ** z;
  const latBogen = (lat * Math.PI) / 180;
  return {
    z,
    x: Math.floor(((lng + 180) / 360) * n),
    y: Math.floor(((1 - Math.log(Math.tan(latBogen) + 1 / Math.cos(latBogen)) / Math.PI) / 2) * n),
  };
}

/** Alle Kacheln im Umkreis, ueber die angegebenen Zoomstufen. */
export function kachelliste(
  lat: number,
  lng: number,
  umkreisM: number,
  vonZoom: number,
  bisZoom: number,
): Kachel[] {
  const gradBreite = umkreisM / 111_320;
  const gradLaenge = umkreisM / (111_320 * Math.cos((lat * Math.PI) / 180));
  const liste: Kachel[] = [];

  for (let z = vonZoom; z <= bisZoom; z++) {
    const nordwest = kachelFuer(lat + gradBreite, lng - gradLaenge, z);
    const suedost = kachelFuer(lat - gradBreite, lng + gradLaenge, z);
    for (let x = nordwest.x; x <= suedost.x; x++) {
      for (let y = nordwest.y; y <= suedost.y; y++) {
        liste.push({ z, x, y });
      }
    }
  }
  return liste;
}

export const kachelUrl = (vorlage: string, kachel: Kachel): string =>
  vorlage
    .replace('{z}', String(kachel.z))
    .replace('{x}', String(kachel.x))
    .replace('{y}', String(kachel.y));

export interface Ladestand {
  geladen: number;
  gesamt: number;
}

/**
 * Kacheln laden. Bricht die Verbindung ab, bleibt liegen, was schon da ist —
 * ein erneuter Aufruf setzt fort, statt von vorne zu beginnen (PV-B17).
 */
export async function ladeKacheln(
  vorlage: string,
  kacheln: Kachel[],
  melde?: (stand: Ladestand) => void,
  abbruch?: AbortSignal,
): Promise<Ladestand> {
  const speicher = await caches.open(KACHELSPEICHER);
  let geladen = 0;
  const gesamt = kacheln.length;
  const gleichzeitig = 6;

  for (let i = 0; i < kacheln.length; i += gleichzeitig) {
    if (abbruch?.aborted) break;
    const buendel = kacheln.slice(i, i + gleichzeitig);
    await Promise.all(
      buendel.map(async (kachel) => {
        const url = kachelUrl(vorlage, kachel);
        if (await speicher.match(url)) {
          geladen++;
          return;
        }
        try {
          const antwort = await fetch(url, { mode: 'cors' });
          if (antwort.ok) {
            await speicher.put(url, antwort.clone());
            geladen++;
          }
        } catch {
          // Ohne Verbindung bleibt die Kachel offen. Der Zustand am Projekt
          // zeigt danach "teilweise geladen" — genau die Information, die vor
          // der Abfahrt gebraucht wird (PV-B02).
        }
      }),
    );
    melde?.({ geladen, gesamt });
  }

  return { geladen, gesamt };
}

/** Wie viele der noetigen Kacheln liegen schon lokal? */
export async function pruefeKacheln(vorlage: string, kacheln: Kachel[]): Promise<Ladestand> {
  const speicher = await caches.open(KACHELSPEICHER);
  let geladen = 0;
  for (const kachel of kacheln) {
    if (await speicher.match(kachelUrl(vorlage, kachel))) geladen++;
  }
  return { geladen, gesamt: kacheln.length };
}

/** Alte Kacheln verwerfen, damit das Geraet nicht volllaeuft (PV-B04). */
export async function raeumeKachelspeicher(behalteUrls: Set<string>): Promise<number> {
  const speicher = await caches.open(KACHELSPEICHER);
  const alle = await speicher.keys();
  let entfernt = 0;
  for (const anfrage of alle) {
    if (!behalteUrls.has(anfrage.url)) {
      await speicher.delete(anfrage);
      entfernt++;
    }
  }
  return entfernt;
}
